import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { FaTimes } from 'react-icons/fa'

import { useApp } from '../contexts/AppContext'
import { useAuth } from '../contexts/AuthContext'
import { shipmentsService } from '../services/shipmentsService'
import OrderModal from '../components/OrderModal.jsx'
import AssignmentModal from '../components/AssignmentModal.jsx'
import EditShipmentModal from '../components/EditShipmentModal.jsx'
import ShipmentLogsDrawer from '../components/ShipmentLogsDrawer.jsx'
import payoutsIcon from '../assets/images/payouts-icon.svg'
import { exportToCSV } from '../utils/csvExport'
import { downloadShipmentInvoice } from '../utils/invoicePdf'
import { getShipmentRequesterName } from '../utils/shipmentHelpers'

const SalesApprovalsPage = () => {
  const { username, role } = useParams()
  const navigate = useNavigate()
  const { shipments, loading, error, dispatch, actions } = useApp()
  const { user } = useAuth()

  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedOrder, setSelectedOrder] = useState(null)
  const [assignmentOrder, setAssignmentOrder] = useState(null)
  const [editOrder, setEditOrder] = useState(null)
  const [dateFilter, setDateFilter] = useState('Recent Requests')
  const [logsDrawer, setLogsDrawer] = useState({ open: false, shipmentId: null })
  
  // Decline reason modal state
  const [showDeclineModal, setShowDeclineModal] = useState(false)
  const [declineOrderId, setDeclineOrderId] = useState(null)
  const [selectedReason, setSelectedReason] = useState('')
  const [customReason, setCustomReason] = useState('')
  const [isDeclining, setIsDeclining] = useState(false)

  // Check if current user is admin
  const isAdmin = user?.email == 'muhammad.asad@cargo360pk.com' || user?.id == 126;

  // Fetch shipments on component mount
  useEffect(() => {
    fetchShipments()
  }, [])

  // Auto-refresh every 10 seconds
useEffect(() => {
  const interval = setInterval(() => {
    fetchShipments();
  }, 60000); // 10 seconds

  return () => clearInterval(interval); // Cleanup on unmount
}, []);


  const fetchShipments = async () => {
    dispatch({ type: actions.SET_SHIPMENTS_LOADING, payload: true })

    try {
      const result = await shipmentsService.getAllShipments()

      if (result.success) {
        dispatch({ type: actions.SET_SHIPMENTS_SUCCESS, payload: result.data })
      } else {
        dispatch({ type: actions.SET_SHIPMENTS_ERROR, payload: result.error })
        toast.error(result.message || 'Failed to fetch shipments')
      }
    } catch (error) {
      dispatch({ type: actions.SET_SHIPMENTS_ERROR, payload: error.message })
      toast.error('An error occurred while fetching shipments')
    }
  }

  // Transform shipments data to match the UI format
  const transformedShipments = shipments.map(shipment => ({
    orderId: `C360-PK-${shipment.id}`,
    userName: getShipmentRequesterName(shipment),
    productName: shipment.cargoType || 'Cargo',
    payout: shipment.budget || 0,
    receiptUrl: '#',
    dateTime: new Date(shipment.createdAt).toLocaleString(),
    status: shipment.status,
    shipmentData: shipment, // Keep original data for reference
  }))

  const filteredRequests = transformedShipments.filter((req) => {
    console.log(req.shipmentData?.Trucker?.name.toLowerCase())
    const matchesStatus = statusFilter === 'all' || req.status === statusFilter

    let matchesUser = true
    if (username && role) {
      // Filter based on role
      if (role === 'customer') {
        // Show orders where this user is the customer
        matchesUser = req.userName.toLowerCase() === username.toLowerCase()
      } else if (role === 'trucker') {
        // Show orders where this user is the assigned trucker/broker
        matchesUser = req.shipmentData?.Trucker?.name.toLowerCase() === username.toLowerCase()
      } else if (role === 'driver') {
        // Show orders where this user is the assigned driver
        matchesUser = req.shipmentData?.Driver?.name.toLowerCase() === username.toLowerCase()
      } else {
        // For other roles, show both customer and trucker matches
        matchesUser = req.userName.toLowerCase() === username.toLowerCase() ||
          req.shipmentData?.Trucker?.name.toLowerCase() === username.toLowerCase() ||
          req.shipmentData?.Driver?.name.toLowerCase() === username.toLowerCase()
      }
    }

    let matchesDate = true
    if (dateFilter === 'Last 15 Days' || dateFilter === 'Last 30 Days') {
      const days = dateFilter === 'Last 15 Days' ? 15 : 30
      const cutoffDate = new Date()
      cutoffDate.setDate(cutoffDate.getDate() - days)
      const reqDate = new Date(req.shipmentData.createdAt)
      matchesDate = reqDate >= cutoffDate
    }

    return matchesUser && matchesDate && matchesStatus
  })

  const handleAccept = async (orderId) => {
    const shipmentId = orderId.replace('C360-PK-', '')

    try {
      const result = await shipmentsService.updateShipmentStatus(shipmentId, 'accepted')

      if (result.success) {
        dispatch({ type: actions.UPDATE_SHIPMENT, payload: result.data })
        toast.success('Shipment accepted successfully')
      } else {
        toast.error(result.message || 'Failed to accept shipment')
      }
    } catch (error) {
      toast.error('An error occurred while accepting shipment')
    }
  }

  const handleDecline = (orderId) => {
    setDeclineOrderId(orderId)
    setShowDeclineModal(true)
    setSelectedReason('')
    setCustomReason('')
  }

  const handleCloseDeclineModal = () => {
    setShowDeclineModal(false)
    setDeclineOrderId(null)
    setSelectedReason('')
    setCustomReason('')
  }

  const handleSubmitDecline = async () => {
    if (!selectedReason) {
      toast.error('Please select a decline reason')
      return
    }

    if (selectedReason === 'Others' && !customReason.trim()) {
      toast.error('Please enter your decline reason')
      return
    }

    if (!declineOrderId) return

    const shipmentId = declineOrderId.replace('C360-PK-', '')
    const declineReason = selectedReason === 'Others' ? customReason.trim() : selectedReason

    try {
      setIsDeclining(true)
      setShowDeclineModal(false)

      const result = await shipmentsService.deleteShipment(shipmentId, declineReason)

      if (result.success) {
        dispatch({ type: actions.DELETE_SHIPMENT, payload: parseInt(shipmentId) })
        toast.success('Shipment declined successfully')
        fetchShipments() // Refresh the list
      } else {
        toast.error(result.message || 'Failed to decline shipment')
        setShowDeclineModal(true) // Reopen modal on error
      }
    } catch (error) {
      console.error('Decline error:', error)
      toast.error(error?.message || 'An error occurred while declining shipment')
      setShowDeclineModal(true) // Reopen modal on error
    } finally {
      setIsDeclining(false)
      if (!showDeclineModal) {
        setDeclineOrderId(null)
        setSelectedReason('')
        setCustomReason('')
      }
    }
  }

  const handleDeleteShipment = async (orderId) => {
    const shipmentId = orderId.replace('C360-PK-', '')

    if (window.confirm('Are you sure you want to delete this shipment?')) {
      try {
        const result = await shipmentsService.deleteShipment(shipmentId)

        if (result.success) {
          dispatch({ type: actions.DELETE_SHIPMENT, payload: parseInt(shipmentId) })
          toast.success('Shipment deleted successfully')
        } else {
          toast.error(result.message || 'Failed to delete shipment')
        }
      } catch (error) {
        toast.error('An error occurred while deleting shipment')
      }
    }
  }

  const handleEditShipment = (req) => {
    setEditOrder({
      orderId: req.orderId,
      shipmentId: req.orderId.replace('C360-PK-', ''),
      customer: req.userName,
      status: req.status,
      shipmentData: req.shipmentData,
    })
  }

  const handlePrintInvoice = async (req) => {
    try {
      await downloadShipmentInvoice(req.shipmentData)
      toast.success('Invoice downloaded successfully')
    } catch (error) {
      console.error('Invoice export error:', error)
      toast.error(error?.message || 'Failed to export invoice')
    }
  }

  const handleUpdateShipment = (updatedShipment) => {
    // Update the shipment in the context
    dispatch({ type: actions.UPDATE_SHIPMENT, payload: updatedShipment })
    fetchShipments() // Refresh the list to get latest data
  }

  const handleRowClick = (userName, userRole) => {
    navigate(`/orders/${userName}/${userRole}`)
  }

  const handleAssignment = (orderId) => {
    const shipmentId = orderId.replace('C360-PK-', '')
    const shipment = filteredRequests.find(req => req.orderId === orderId)

    if (shipment) {
      setAssignmentOrder({
        orderId: orderId,
        shipmentId: shipmentId,
        customer: shipment.userName,
        status: shipment.status,
        shipmentData: shipment.shipmentData
      })
    }
  }

  const handleAssignSubmit = async (orderId, assignmentType, userId) => {
    const shipmentId = orderId.replace('C360-PK-', '')

    try {
      const result = await shipmentsService.assignShipment(shipmentId, assignmentType, userId)

      if (result.success) {
        dispatch({ type: actions.UPDATE_SHIPMENT, payload: result.data })
        toast.success(`Shipment assigned to ${assignmentType} successfully`)
        setAssignmentOrder(null)
      } else {
        toast.error(result.message || `Failed to assign shipment to ${assignmentType}`)
      }
    } catch (error) {
      toast.error(`An error occurred while assigning shipment to ${assignmentType}`)
    }
  }

  const handleViewLogs = (shipmentId) => {
    setLogsDrawer({
      open: true,
      shipmentId
    })
  }

  const handleExportCSV = () => {
    const headers = [
      { label: 'Shipment ID', key: 'orderId' },
      { label: 'Customer Name', key: 'userName' },
      { label: 'Cargo Type', key: 'productName' },
      { label: 'Pickup Location', key: 'shipmentData.pickupLocation' },
      { label: 'Drop Location', key: 'shipmentData.dropLocation' },
      { label: 'Date & Time', key: 'dateTime' },
      { label: 'Status', key: 'status' },
    ]

    const filename = `orders_${new Date().toISOString().split('T')[0]}.csv`
    exportToCSV(filteredRequests, headers, filename)
  }

  const statusBadge = (status) => {
    const map = {
      delivered:  'bg-[#eafaf1] text-[#27ae60]',
      accepted:   'bg-[#eaf4fb] text-[#3498db]',
      in_transit: 'bg-[#fef5e7] text-[#e67e22]',
      picked_up:  'bg-[#fef5e7] text-[#e67e22]',
      cancelled:  'bg-[#fdedec] text-[#e74c3c]',
    }
    return map[status] || 'bg-[#f8f9fa] text-[#7f8c8d]'
  }

  const rowBg = (status) => {
    if (status === 'delivered' || status === 'accepted') return 'bg-[#f0fdf4]'
    if (status === 'cancelled') return 'bg-[#fff5f5]'
    return 'hover:bg-[#f8f9fa]'
  }

  return (
    <div className="px-[20px] md:px-[40px] py-[30px] min-h-screen bg-[#f8f9fa]">
      {/* Page Header */}
      <div className="flex items-center justify-between pb-5 mb-6 border-b border-[#e9ecef]">
        <div className="flex items-center gap-3">
          {username && (
            <button onClick={() => navigate(-1)} className="text-[#7f8c8d] hover:text-[#2c3e50] transition-colors">←</button>
          )}
          <h1 className="text-[1.8rem] font-semibold text-[#2c3e50]">
            {username
              ? `Orders — ${username}${role ? ` (${role === 'trucker' ? 'Broker' : role.charAt(0).toUpperCase() + role.slice(1)})` : ''}`
              : 'Orders'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="border border-[#e9ecef] rounded-lg px-4 py-2 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]">
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="accepted">Accepted</option>
            <option value="picked_up">Picked Up</option>
            <option value="in_transit">In Transit</option>
            <option value="delivered">Delivered</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <select value={dateFilter} onChange={(e) => setDateFilter(e.target.value)}
            className="border border-[#e9ecef] rounded-lg px-4 py-2 text-sm text-[#2c3e50] focus:outline-none focus:border-[#3498db]">
            <option>Recent Requests</option>
            <option>Last 15 Days</option>
            <option>Last 30 Days</option>
          </select>
          <button onClick={handleExportCSV}
            className="bg-[#3498db] hover:bg-[#2980b9] text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
            Export CSV
          </button>
        </div>
      </div>

      {/* Error */}
      {error.shipments && (
        <div className="bg-[#fdedec] border border-[#e74c3c]/30 text-[#e74c3c] px-4 py-3 rounded-lg mb-5 text-sm">
          {error.shipments}
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-[#e9ecef] rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead>
            <tr className="border-b border-[#e9ecef] bg-[#f8f9fa]">
              {['Shipment ID', 'Customer', 'Cargo Type', 'Pickup', 'Drop', 'Date', 'Status', 'Details', 'Actions'].map(h => (
                <th key={h} className="text-left px-5 py-3 text-xs font-medium text-[#7f8c8d] uppercase tracking-wide">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredRequests.map((req) => (
              <tr
                key={req.orderId}
                className={`border-b border-[#e9ecef] cursor-pointer transition-colors ${rowBg(req.status)}`}
                onClick={() => !username && handleRowClick(req.userName, 'customer')}
              >
                <td className="px-5 py-3 font-mono text-xs text-[#2c3e50]">{req.orderId}</td>
                <td className="px-5 py-3 text-[#2c3e50] font-medium">{req.userName}</td>
                <td className="px-5 py-3 text-[#7f8c8d]">{req.productName}</td>
                <td className="px-5 py-3 text-[#7f8c8d] text-xs max-w-[140px] truncate">{req.shipmentData.pickupLocation}</td>
                <td className="px-5 py-3 text-[#7f8c8d] text-xs max-w-[140px] truncate">{req.shipmentData.dropLocation}</td>
                <td className="px-5 py-3 text-[#7f8c8d] text-xs whitespace-nowrap">{req.dateTime}</td>
                <td className="px-5 py-3">
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${statusBadge(req.status)}`}>
                    {req.status.replace('_', ' ').toUpperCase()}
                  </span>
                </td>
                <td className="px-5 py-3">
                  <div className="flex gap-1.5">
                    <button
                      className="px-2.5 py-1 bg-[#f0e6ff] text-[#8e44ad] hover:bg-[#8e44ad] hover:text-white rounded text-xs font-medium transition-colors"
                      onClick={(e) => { e.stopPropagation(); setSelectedOrder({ orderId: req.orderId, customer: req.userName, shipmentData: req.shipmentData }) }}
                    >Quick View</button>
                    <button
                      className="px-2.5 py-1 bg-[#e8f8f5] text-[#16a085] hover:bg-[#16a085] hover:text-white rounded text-xs font-medium transition-colors"
                      onClick={(e) => { e.stopPropagation(); handleViewLogs(req.shipmentData.id) }}
                    >Logs</button>
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="flex flex-wrap gap-1.5">
                    {req.status?.toLowerCase() !== 'cancelled' && (
                      <button onClick={(e) => { e.stopPropagation(); handleDecline(req.orderId) }}
                        className="px-2.5 py-1 bg-[#fdedec] text-[#e74c3c] hover:bg-[#e74c3c] hover:text-white rounded text-xs font-medium transition-colors">
                        Decline
                      </button>
                    )}
                    {req.status === 'pending' && (
                      <button onClick={(e) => { e.stopPropagation(); handleAccept(req.orderId) }}
                        className="px-2.5 py-1 bg-[#eafaf1] text-[#27ae60] hover:bg-[#27ae60] hover:text-white rounded text-xs font-medium transition-colors">
                        Accept
                      </button>
                    )}
                    {(req.status === 'accepted' || req.status === 'confirmed') && (
                      <button onClick={(e) => { e.stopPropagation(); handleAssignment(req.orderId) }}
                        className="px-2.5 py-1 bg-[#eafaf1] text-[#27ae60] hover:bg-[#27ae60] hover:text-white rounded text-xs font-medium transition-colors">
                        Assign
                      </button>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); handlePrintInvoice(req) }}
                      className="px-2.5 py-1 bg-[#fff3e9] text-[#ee8525] hover:bg-[#ee8525] hover:text-white rounded text-xs font-medium transition-colors">
                      Print Invoice
                    </button>
                    {isAdmin && (
                      <>
                        <button onClick={(e) => { e.stopPropagation(); handleEditShipment(req) }}
                          className="px-2.5 py-1 bg-[#eaf4fb] text-[#3498db] hover:bg-[#3498db] hover:text-white rounded text-xs font-medium transition-colors">
                          Edit
                        </button>
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteShipment(req.orderId) }}
                          className="px-2.5 py-1 bg-[#fdedec] text-[#e74c3c] hover:bg-[#e74c3c] hover:text-white rounded text-xs font-medium transition-colors">
                          Delete
                        </button>
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedOrder && <OrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />}
      {assignmentOrder && <AssignmentModal shipment={assignmentOrder} onClose={() => setAssignmentOrder(null)} onAssign={handleAssignSubmit} />}
      {editOrder && <EditShipmentModal shipment={editOrder} onClose={() => setEditOrder(null)} onUpdate={handleUpdateShipment} />}
      <ShipmentLogsDrawer open={logsDrawer.open} shipmentId={logsDrawer.shipmentId} onClose={() => setLogsDrawer({ open: false, shipmentId: null })} />

      {/* Decline Reason Modal */}
      {showDeclineModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={handleCloseDeclineModal}>
          <div className="bg-white rounded-xl w-[480px] max-h-[90vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="bg-[#2c3e50] px-6 py-4 rounded-t-xl flex items-center justify-between">
              <h3 className="text-white font-semibold flex items-center gap-2">
                <FaTimes /> Decline Request
              </h3>
              <button onClick={handleCloseDeclineModal} disabled={isDeclining}
                className="text-white/70 hover:text-white transition-colors disabled:opacity-50">
                <FaTimes size={18} />
              </button>
            </div>

            <div className="p-6">
              <h4 className="text-[#2c3e50] font-semibold mb-1">Why are you declining this request?</h4>
              <p className="text-[#7f8c8d] text-sm mb-5">Please select a reason *</p>

              <div className="space-y-3 mb-5">
                {['Not satisfied with the rates', "Don't need Booking anymore", 'Others'].map((reason) => (
                  <button key={reason} type="button"
                    onClick={() => { setSelectedReason(reason); if (reason !== 'Others') setCustomReason('') }}
                    className={`w-full text-left px-4 py-3.5 rounded-lg border-2 text-sm transition-all ${
                      selectedReason === reason
                        ? 'border-[#3498db] bg-[#eaf4fb] text-[#2c3e50] font-semibold'
                        : 'border-[#e9ecef] text-[#2c3e50] hover:border-[#3498db]'
                    }`}
                  >
                    {reason}
                  </button>
                ))}
              </div>

              {selectedReason === 'Others' && (
                <div className="mb-5">
                  <label className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1.5 block">Specify reason *</label>
                  <textarea
                    className="w-full px-4 py-3 border border-[#e9ecef] rounded-lg focus:outline-none focus:border-[#3498db] text-sm text-[#2c3e50] resize-none"
                    placeholder="Enter your decline reason"
                    rows={4}
                    value={customReason}
                    onChange={(e) => setCustomReason(e.target.value)}
                  />
                </div>
              )}

              <div className="flex gap-3 pt-4 border-t border-[#e9ecef]">
                <button type="button" onClick={handleCloseDeclineModal} disabled={isDeclining}
                  className="flex-1 px-5 py-2.5 border border-[#e9ecef] rounded-lg text-sm font-medium text-[#7f8c8d] hover:bg-[#f8f9fa] transition-colors disabled:opacity-50">
                  Cancel
                </button>
                <button type="button" onClick={handleSubmitDecline}
                  disabled={isDeclining || !selectedReason || (selectedReason === 'Others' && !customReason.trim())}
                  className="flex-1 px-5 py-2.5 bg-[#e74c3c] hover:bg-[#c0392b] text-white rounded-lg text-sm font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                  {isDeclining ? 'Declining…' : 'Confirm Decline'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SalesApprovalsPage



// This commented code has the decline/cancel reason taking modal code
// import React, { useState, useEffect } from 'react'
// import { useNavigate, useParams } from 'react-router-dom'
// import { toast } from 'react-toastify'

// import { useApp } from '../contexts/AppContext'
// import { useAuth } from '../contexts/AuthContext'
// import { shipmentsService } from '../services/shipmentsService'
// import OrderModal from '../components/OrderModal.jsx'
// import AssignmentModal from '../components/AssignmentModal.jsx'
// import EditShipmentModal from '../components/EditShipmentModal.jsx'
// import payoutsIcon from '../assets/images/payouts-icon.svg'

// const SalesApprovalsPage = () => {
//   const { username } = useParams()
//   const navigate = useNavigate()
//   const { shipments, loading, error, dispatch, actions } = useApp()
//   const { user } = useAuth()

//   const [statusFilter, setStatusFilter] = useState('all')
//   const [selectedOrder, setSelectedOrder] = useState(null)
//   const [assignmentOrder, setAssignmentOrder] = useState(null)
//   const [editOrder, setEditOrder] = useState(null)
//   const [dateFilter, setDateFilter] = useState('Recent Requests')

//   // Cancel reason modal
//   const [showCancelModal, setShowCancelModal] = useState(false)
//   const [cancelReason, setCancelReason] = useState('')
//   const [currentDeclineOrder, setCurrentDeclineOrder] = useState(null)
//   const [showReasonModal, setShowReasonModal] = useState(false)
//   const [reasonToShow, setReasonToShow] = useState('')

//   // Check if current user is admin
//   const isAdmin = user?.email == 'muhammad.asad@cargo360pk.com' || user?.id == 126;

//   // Fetch shipments on component mount
//   useEffect(() => {
//     fetchShipments()
//   }, [])

//   // Auto-refresh every 60 seconds
//   useEffect(() => {
//     const interval = setInterval(() => {
//       fetchShipments();
//     }, 60000);

//     return () => clearInterval(interval);
//   }, []);

//   const fetchShipments = async () => {
//     dispatch({ type: actions.SET_SHIPMENTS_LOADING, payload: true })

//     try {
//       const result = await shipmentsService.getAllShipments()

//       if (result.success) {
//         dispatch({ type: actions.SET_SHIPMENTS_SUCCESS, payload: result.data })
//       } else {
//         dispatch({ type: actions.SET_SHIPMENTS_ERROR, payload: result.error })
//         toast.error(result.message || 'Failed to fetch shipments')
//       }
//     } catch (error) {
//       dispatch({ type: actions.SET_SHIPMENTS_ERROR, payload: error.message })
//       toast.error('An error occurred while fetching shipments')
//     }
//   }

//   // Transform shipments data to match the UI format
//   const transformedShipments = shipments.map(shipment => ({
//     orderId: `C360-PK-${shipment.id}`,
//     userName: shipment.Customer?.name || 'Unknown Customer',
//     productName: shipment.cargoType || 'Cargo',
//     payout: shipment.budget || 0,
//     receiptUrl: '#',
//     dateTime: new Date(shipment.createdAt).toLocaleString(),
//     status: shipment.status,
//     cancelReason: shipment.cancelReason || '',
//     shipmentData: shipment,
//   }))

//   const filteredRequests = transformedShipments.filter((req) => {
//     const matchesStatus = statusFilter === 'all' || req.status === statusFilter

//     const matchesUser = username
//       ? req.userName.toLowerCase() === username.toLowerCase() ||
//       req.shipmentData?.Trucker?.name.toLowerCase() === username.toLowerCase()
//       : true

//     let matchesDate = true
//     if (dateFilter === 'Last 15 Days' || dateFilter === 'Last 30 Days') {
//       const days = dateFilter === 'Last 15 Days' ? 15 : 30
//       const cutoffDate = new Date()
//       cutoffDate.setDate(cutoffDate.getDate() - days)
//       const reqDate = new Date(req.shipmentData.createdAt)
//       matchesDate = reqDate >= cutoffDate
//     }

//     return matchesUser && matchesDate && matchesStatus
//   })

//   const handleAccept = async (orderId) => {
//     const shipmentId = orderId.replace('C360-PK-', '')

//     try {
//       const result = await shipmentsService.updateShipmentStatus(shipmentId, 'accepted')

//       if (result.success) {
//         dispatch({ type: actions.UPDATE_SHIPMENT, payload: result.data })
//         toast.success('Shipment accepted successfully')
//       } else {
//         toast.error(result.message || 'Failed to accept shipment')
//       }
//     } catch (error) {
//       toast.error('An error occurred while accepting shipment')
//     }
//   }

//   const handleDeclineClick = (orderId) => {
//     setCurrentDeclineOrder(orderId)
//     setCancelReason('')
//     setShowCancelModal(true)
//   }

//   const handleDeclineConfirm = async () => {
//     const shipmentId = currentDeclineOrder.replace('C360-PK-', '')

//     try {
//       // Update locally; integrate backend later if needed
//       dispatch({
//         type: actions.UPDATE_SHIPMENT,
//         payload: {
//           ...shipments.find(s => `C360-PK-${s.id}` === currentDeclineOrder),
//           status: 'cancelled',
//           cancelReason: cancelReason
//         }
//       })

//       toast.success('Shipment declined successfully')
//       setShowCancelModal(false)
//     } catch (error) {
//       toast.error('An error occurred while declining shipment')
//     }
//   }

//   const handleDeleteShipment = async (orderId) => {
//     const shipmentId = orderId.replace('C360-PK-', '')

//     if (window.confirm('Are you sure you want to delete this shipment?')) {
//       try {
//         const result = await shipmentsService.deleteShipment(shipmentId)

//         if (result.success) {
//           dispatch({ type: actions.DELETE_SHIPMENT, payload: parseInt(shipmentId) })
//           toast.success('Shipment deleted successfully')
//         } else {
//           toast.error(result.message || 'Failed to delete shipment')
//         }
//       } catch (error) {
//         toast.error('An error occurred while deleting shipment')
//       }
//     }
//   }

//   const handleEditShipment = (req) => {
//     setEditOrder({
//       orderId: req.orderId,
//       shipmentId: req.orderId.replace('C360-PK-', ''),
//       customer: req.userName,
//       status: req.status,
//       shipmentData: req.shipmentData,
//     })
//   }

//   const handleUpdateShipment = (updatedShipment) => {
//     dispatch({ type: actions.UPDATE_SHIPMENT, payload: updatedShipment })
//     fetchShipments()
//   }

//   const handleRowClick = (userName) => {
//     navigate(`/orders/${userName}`)
//   }

//   const handleAssignment = (orderId) => {
//     const shipmentId = orderId.replace('C360-PK-', '')
//     const shipment = filteredRequests.find(req => req.orderId === orderId)

//     if (shipment) {
//       setAssignmentOrder({
//         orderId: orderId,
//         shipmentId: shipmentId,
//         customer: shipment.userName,
//         status: shipment.status,
//         shipmentData: shipment.shipmentData
//       })
//     }
//   }

//   const handleAssignSubmit = async (orderId, assignmentType, userId) => {
//     const shipmentId = orderId.replace('C360-PK-', '')

//     try {
//       const result = await shipmentsService.assignShipment(shipmentId, assignmentType, userId)

//       if (result.success) {
//         dispatch({ type: actions.UPDATE_SHIPMENT, payload: result.data })
//         toast.success(`Shipment assigned to ${assignmentType} successfully`)
//         setAssignmentOrder(null)
//       } else {
//         toast.error(result.message || `Failed to assign shipment to ${assignmentType}`)
//       }
//     } catch (error) {
//       toast.error(`An error occurred while assigning shipment to ${assignmentType}`)
//     }
//   }

//   return (
//     <div className="min-h-screen flex flex-col">
//       <main className="flex-1 px-[94px] py-[30px]">
//         <div className="flex justify-between px-[12px]">
//           <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
//             {username && (
//               <button
//                 onClick={() => navigate(-1)}
//                 className="text-gray-600 hover:text-black"
//               >
//                 ←
//               </button>
//             )}
//             <span className="text-purple-600">
//               <img src={payoutsIcon} alt="icon" />
//             </span>
//             <span className="modal-heading">
//               {username ? `Orders (${username})` : 'Orders'}
//             </span>
//           </h2>

//           <div className="flex gap-[20px] mb-4">
//             <select
//               value={statusFilter}
//               onChange={(e) => setStatusFilter(e.target.value)}
//               className="border rounded px-[14px] py-[10px] filter-button filter-button-border focus:outline-none"
//             >
//               <option value="all">All Status</option>
//               <option value="pending">Pending</option>
//               <option value="accepted">Accepted</option>
//               <option value="picked_up">Picked Up</option>
//               <option value="in_transit">In Transit</option>
//               <option value="delivered">Delivered</option>
//               <option value="cancelled">Cancelled</option>
//             </select>
//             <select
//               value={dateFilter}
//               onChange={(e) => setDateFilter(e.target.value)}
//               className="border rounded px-[14px] py-[10px] filter-button filter-button-border focus:outline-none"
//             >
//               <option>Recent Requests</option>
//               <option>Last 15 Days</option>
//               <option>Last 30 Days</option>
//             </select>
//             {!username && (
//               <button
//                 className="text-blueBrand-normal filter-button"
//                 onClick={() => {
//                   setStatusFilter('all')
//                   setDateFilter('Recent Requests')
//                   fetchShipments()
//                 }}
//               >
//                 Refresh
//               </button>
//             )}
//           </div>
//         </div>

//         {/* Loading state */}
//         {/* {loading.shipments && (
//           <div className="flex justify-center items-center py-8">
//             <div className="text-gray-500">Loading shipments...</div>
//           </div>
//         )} */}

//         {/* Error state */}
//         {error.shipments && (
//           <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
//             {error.shipments}
//           </div>
//         )}

//         <div className="overflow-x-auto bg-white rounded shadow">
//           <table className="min-w-full text-sm text-left">
//             <thead className="bg-[#F6F5F6] text-blueBrand-normal text-xs font-semibold">
//               <tr className="filter-button leading-[18px]">
//                 <th className="px-4 py-3">Shipment ID</th>
//                 <th className="px-4 py-3">Customer Name</th>
//                 <th className="px-4 py-3">Cargo Type</th>
//                 <th className="px-4 py-3">Pickup Location</th>
//                 <th className="px-4 py-3">Drop Location</th>
//                 <th className="px-4 py-3">Date & Time</th>
//                 <th className="px-4 py-3">Status</th>
//                 <th className="px-4 py-3">Details</th>
//                 <th className="px-4 py-3">Action</th>
//               </tr>
//             </thead>
//             <tbody className="text-blueBrand-dark">
//               {filteredRequests.map((req) => (
//                 <tr
//                   key={req.orderId}
//                   className={`border-t cursor-pointer ${req.status === 'delivered' || req.status === 'accepted'
//                     ? 'bg-[#ABEFC6]'
//                     : req.status === 'cancelled'
//                       ? 'bg-[#FDECEC]'
//                       : 'hover:bg-gray-50'
//                     }`}
//                   onClick={() => !username && handleRowClick(req.userName)}
//                 >
//                   <td className="px-[24px] py-[16px] form-subheading">{req.orderId}</td>
//                   <td className="px-[24px] py-[16px] form-subheading">{req.userName}</td>
//                   <td className="px-[24px] py-[16px] form-subheading">{req.productName}</td>
//                   <td className="px-[24px] py-[16px] form-subheading text-xs">{req.shipmentData.pickupLocation?.substring(0, 30)}...</td>
//                   <td className="px-[24px] py-[16px] form-subheading text-xs">{req.shipmentData.dropLocation?.substring(0, 30)}...</td>
//                   <td className="px-[24px] py-[16px] text-blueBrand-normal form-subheading" style={{ lineHeight: '20px' }}>
//                     {req.dateTime.split(/(?<=\d{4})\s/).map((part, i) => (<div key={i}>{part}</div>))}
//                   </td>
//                   <td className="px-[24px] py-[16px]">
//                     <span className={`px-2 py-1 rounded text-xs font-semibold ${req.status === 'delivered' ? 'bg-green-100 text-green-800' :
//                       req.status === 'accepted' ? 'bg-blue-100 text-blue-800' :
//                         req.status === 'in_transit' ? 'bg-yellow-100 text-yellow-800' :
//                           req.status === 'picked_up' ? 'bg-orange-100 text-orange-800' :
//                             req.status === 'cancelled' ? 'bg-red-100 text-red-800' :
//                               'bg-gray-100 text-gray-800'
//                       }`}>
//                       {req.status.replace('_', ' ').toUpperCase()}
//                     </span>
//                   </td>
//                   <td className="px-[24px] py-[16px]">
//                     <button className="text-purple-600 underline text-sm"
//                       onClick={() => setSelectedOrder({ orderId: req.orderId, customer: req.userName, shipmentData: req.shipmentData })}>
//                       View Details
//                     </button>
//                   </td>
//                   <td className="px-[24px] py-[16px]">
//                     <div className="flex gap-2">
//                       {req.status?.toLowerCase() !== 'cancelled' && (
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation()
//                             handleDeclineClick(req.orderId)
//                           }}
//                           className="action-button text-[#DC3434] hover:underline text-xs"
//                         >
//                           Decline
//                         </button>
//                       )}

//                       {req.status === 'cancelled' && req.cancelReason && (
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation()
//                             setReasonToShow(req.cancelReason)
//                             setShowReasonModal(true)
//                           }}
//                           className="action-button text-[#DC3434] hover:underline text-xs"
//                         >
//                           Cancel Reason
//                         </button>
//                       )}

//                       {req.status === 'pending' && (
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation()
//                             handleAccept(req.orderId)
//                           }}
//                           className="action-button text-[#17B26A] hover:underline text-xs"
//                         >
//                           Accept
//                         </button>
//                       )}

//                       {req.status === 'accepted' && (
//                         <button
//                           onClick={(e) => {
//                             e.stopPropagation()
//                             handleAssignment(req.orderId)
//                           }}
//                           className="action-button text-[#17B26A] hover:underline text-xs"
//                         >
//                           Assign broker/driver
//                         </button>
//                       )}

//                       {isAdmin && (
//                         <>
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation()
//                               handleEditShipment(req)
//                             }}
//                             className="action-button text-[#17B26A] hover:underline text-xs"
//                           >
//                             Edit
//                           </button>
//                           <button
//                             onClick={(e) => {
//                               e.stopPropagation()
//                               handleDeleteShipment(req.orderId)
//                             }}
//                             className="action-button text-[#DC3434] hover:underline text-xs"
//                           >
//                             Delete
//                           </button>
//                         </>
//                       )}
//                     </div>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </main>

//       {/* Selected order modal */}
//       {selectedOrder && (
//         <OrderModal order={selectedOrder} onClose={() => setSelectedOrder(null)} />
//       )}

//       {/* Assignment modal */}
//       {assignmentOrder && (
//         <AssignmentModal shipment={assignmentOrder} onClose={() => setAssignmentOrder(null)} onAssign={handleAssignSubmit} />
//       )}

//       {/* Edit modal */}
//       {editOrder && (
//         <EditShipmentModal shipment={editOrder} onClose={() => setEditOrder(null)} onUpdate={handleUpdateShipment} />
//       )}

//       {/* Cancel Reason Modal */}
//       {showCancelModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
//           <div className="bg-white p-6 rounded shadow-lg w-96">
//             <h3 className="text-lg font-semibold mb-4">Enter Cancel Reason</h3>
//             <textarea
//               value={cancelReason}
//               onChange={(e) => setCancelReason(e.target.value)}
//               className="w-full border rounded p-2 mb-4"
//               placeholder="Enter reason for declining"
//             />
//             <div className="flex justify-end gap-2">
//               <button
//                 className="px-4 py-2 border rounded"
//                 onClick={() => setShowCancelModal(false)}
//               >
//                 Cancel
//               </button>
//               <button
//                 className="px-4 py-2 bg-red-500 text-white rounded"
//                 onClick={handleDeclineConfirm}
//               >
//                 Decline
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//       {/* Show Cancel Reason Modal */}
//       {showReasonModal && (
//         <div className="fixed inset-0 flex items-center justify-center bg-black/30 z-50">
//           <div className="bg-white p-6 rounded shadow-lg w-96">
//             <h3 className="text-lg font-semibold mb-4">Cancel Reason</h3>
//             <p>{reasonToShow}</p>
//             <div className="flex justify-end mt-4">
//               <button
//                 className="px-4 py-2 border rounded"
//                 onClick={() => setShowReasonModal(false)}
//               >
//                 Close
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//     </div>
//   )
// }

// export default SalesApprovalsPage
